import React from "react";
import {
    Grid,
    Chip,
    Typography,
    Divider,
    Button,
    Tooltip,
    CircularProgress
} from "@material-ui/core";
import { green, orange, red, grey } from "@material-ui/core/colors";
import DirectionsBusIcon from "@material-ui/icons/DirectionsBus";
import BatteryFullIcon from "@material-ui/icons/BatteryFull";
import getTrackingStatusInfo, { TRACKER_STATUS } from "../DeviceTracking/getTrackingStatusInfo";
import { useTranslation } from "react-i18next";
import CheckIcon from "@material-ui/icons/Check";
import WarningIcon from "@material-ui/icons/Warning";
import ErrorIcon from "@material-ui/icons/Error";
import Collapse from "@material-ui/core/Collapse";
import IconButton from "@material-ui/core/IconButton";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import FilterListIcon from "@material-ui/icons/FilterList";
import WifiIcon from "@material-ui/icons/Wifi";
import WifiOffIcon from "@material-ui/icons/WifiOff";
import {
    TRACKER_BATTERY,
    getTrackerBatteryInfoByStatus
} from "../DeviceTracking/getTrackerBatteryInfo";
import FilterRow from "./FilterRow";
import { CsvIcon, ExcelIcon } from "./ExportIcons";


const DEFIBRILLATOR_FILTERS = [
    {
        value: "all",
        translation: "common.all",
        icon: <CheckIcon />,
        color: grey[700]
    },
    {
        value: "ok",
        translation: "common.correct",
        icon: <CheckIcon />,
        color: green[600]
    },
    {
        value: "warning",
        translation: "common.warning",
        icon: <WarningIcon />,
        color: orange[500]
    },
    {
        value: "error",
        translation: "common.error",
        icon: <ErrorIcon />,
        color: red[600]
    }
];

const TRACKER_FILTERS = [
    {
        value: "all",
        translation: "common.all"
    },
    {
        value: TRACKER_STATUS.ACTIVE,
        translation: "devices.tracking.config.status.active"
    },
    {
        value: TRACKER_STATUS.IN_USE,
        translation: "devices.tracking.config.status.in_use"
    },
    {
        value: TRACKER_STATUS.EXTRACTED,
        translation: "devices.tracking.config.status.retired"
    },
    {
        value: TRACKER_STATUS.MAINTENANCE,
        translation: "devices.tracking.config.status.maintenance"
    },
    {
        value: TRACKER_STATUS.NO_SIGNAL,
        translation: "devices.tracking.config.status.no_signal"
    }
];

const BATTERY_FILTERS = [
    {
        value: "all",
        translation: "common.all"
    },
    {
        value: TRACKER_BATTERY.VERY_HIGH,
        translation: "devices.filters.battery.very_high"
    },
    {
        value: TRACKER_BATTERY.HIGH,
        translation: "devices.filters.battery.high"
    },
    {
        value: TRACKER_BATTERY.MEDIUM,
        translation: "devices.filters.battery.medium"
    },
    {
        value: TRACKER_BATTERY.LOW,
        translation: "devices.filters.battery.low"
    },
    {
        value: TRACKER_BATTERY.CRITICAL,
        translation: "devices.filters.battery.critical"
    },
    {
        value: TRACKER_BATTERY.EMPTY,
        translation: "devices.filters.battery.empty"
    },
    {
        value: TRACKER_BATTERY.UNKNOWN,
        translation: "devices.filters.battery.not_available"
    }
];

const COMMUNICATION_FILTERS = [
    {
        value: "all",
        translation: "common.all",
        icon: <WifiIcon />,
        color: grey[700]
    },
    {
        value: "blue",
        translation: "connection.ready",
        icon: <WifiIcon />,
        color: "#3f51b5" // secondary
    },
    {
        value: "red",
        translation: "connection.not_ready_default",
        icon: <WifiOffIcon />,
        color: red[600]
    },
    {
        value: "grey",
        translation: "connection.not_module",
        icon: <WifiOffIcon />,
        color: grey[400]
    }
];

const DeviceFilters = ({ 
    filters, 
    onChange, 
    onExportCsv, 
    onExportExcel, 
    exportingCsv,
    exportingExcel,
    hasNormalDevices, 
    hasEmtViewRole, 
    hasEmtAdminRole 
}) => {
    const { t } = useTranslation();
    const [openFilters, setOpenFilters] = React.useState(false);
    
    const toggleFilter = (name, value) => {
        const values = [...filters[name]];
        const index = values.indexOf(value);

        if (index >= 0) {
            values.splice(index, 1);
        } else {
            values.push(value);
        }

        onChange({
            ...filters,
            [name]: values
        });
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Grid
                        item
                        onClick={() => setOpenFilters(!openFilters)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            cursor: "pointer"
                        }}
                    >
                        <FilterListIcon />
                        <Typography variant="h6">
                            Filtros
                        </Typography>
                    </Grid>

                    <Grid
                        item
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8
                        }}
                    >
                        <Tooltip title={t("devices.export.csv")}>
                            <IconButton
                                size="small"
                                onClick={onExportCsv}
                                disabled={exportingCsv}
                            >
                                {exportingCsv ? (
                                    <CircularProgress size={22} />
                                ) : (
                                    <CsvIcon />
                                )}
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={t("devices.export.excel")}>
                            <IconButton
                                size="small"
                                onClick={onExportExcel}
                                disabled={exportingExcel}
                            >
                                {exportingExcel ? (
                                    <CircularProgress size={22} />
                                ) : (
                                    <ExcelIcon />
                                )}
                            </IconButton>
                        </Tooltip>

                        <IconButton
                            size="small"
                            onClick={() => setOpenFilters(!openFilters)}
                        >
                            {openFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Collapse in={openFilters}>
                    <Grid container spacing={2}>
                        <FilterRow
                            title={t("devices.filters.device.title")}
                            filterName="deviceStatus"
                            filters={DEFIBRILLATOR_FILTERS}
                            selectedFilters={filters}
                            onChange={onChange}
                            toggleFilter={toggleFilter}
                            getIcon={(filter) =>
                                React.cloneElement(filter.icon, {
                                    style: {
                                        color:
                                            filter.value === "all"
                                                ? undefined
                                                : filter.color
                                    }
                                })
                            }
                            getLabel={(filter) => t(filter.translation)}
                        />
                        
                        <FilterRow
                            title={t("devices.filters.tracker.title")}
                            filterName="trackerStatus"
                            filters={TRACKER_FILTERS}
                            selectedFilters={filters}
                            onChange={onChange}
                            toggleFilter={toggleFilter}
                            getIcon={(filter) => {
                                const info = filter.value === "all"
                                    ? null
                                    : getTrackingStatusInfo(filter.value, t);

                                return (
                                    <DirectionsBusIcon
                                        style={{
                                            color: info ? info.iconColor : undefined
                                        }}
                                    />
                                );
                            }}
                            getLabel={(filter) => t(filter.translation)}
                        />

                        {!hasEmtAdminRole && (
                            <FilterRow
                                title={t("devices.filters.battery.title")}
                                filterName="trackerBattery"
                                filters={BATTERY_FILTERS}
                                selectedFilters={filters}
                                onChange={onChange}
                                toggleFilter={toggleFilter}
                                getIcon={(filter) => {
                                    if (filter.value === "all") {
                                        return <BatteryFullIcon />;
                                    }

                                    const batteryInfo = getTrackerBatteryInfoByStatus(filter.value);

                                    return (
                                        <batteryInfo.Icon
                                            style={{
                                                color: batteryInfo.color
                                            }}
                                        />
                                    );
                                }}
                                getLabel={(filter) => t(filter.translation)}
                            />
                        )}

                        {!hasEmtAdminRole && (
                        <FilterRow
                            title={t("devices.filters.conection.title")}
                            filterName="communicationStatus"
                            filters={COMMUNICATION_FILTERS}
                            selectedFilters={filters}
                            onChange={onChange}
                            toggleFilter={toggleFilter}
                            getIcon={(filter) =>
                                React.cloneElement(filter.icon, {
                                    style: {
                                        color:
                                            filter.value === "all"
                                                ? undefined
                                                : filter.color
                                    }
                                })
                            }
                            getLabel={(filter) => t(filter.translation)}
                        />
                        )}
                    </Grid>
                </Collapse>
            </Grid>       
        </Grid>
    );
};

export default DeviceFilters;